import React, { SyntheticEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import { TwButton, TwButtonType } from '../../../components/TwButtons/TwButtons';
import TwDialog from '../../../components/TwDialog/TwDialog';
import TwTable, { TwTableColumn, TwTableData } from '../../../components/TwTable/TwTable';
import usePageTitle from '../../../hooks/usePageTitle';
import { ClientResponse, ISearchClientParams } from '../../../redux/api/customer/customer.model';
import { ReactComponent as WarningSign } from '../../../assets/warning-sign.svg';
import TwPaginator from '../../../components/TwPaginator/TwPaginator';
import { BaseQueryParams } from '../../../providers/Global.model';
import TwGenericMenu, {
  TwGenericMenuOptions
} from '../../../components/TwGenericMenu/TwGenericMenu';
import StringUtils from '../../../utils/stringUtils';
import TwBackdrop from '../../../components/TwBackdrop/TwBackdrop';
import TwNameColumn from '../../../components/TwTable/TwNameColumn';
import {
  useDeleteCustomerMutation,
  useSearchCustomersQuery
} from '../../../redux/api/customer/customerApi';
import useNotifications from '../../../providers/Notifications/Notifications.provider';
import TwFilters, { TwFilterTypes } from '../../../components/TwFilters/TwFilters';
import useTwClient from '../../../hooks/useTwCustomer';
import useFeatureFlags from '../../../providers/FeatureFlags/FeatureFlags.provider';
import { USER_STATUS } from '../../../providers/User/User.model';
import TwBadge from '../../../components/TwBadge/TwBadge';
import { logRocketTrack } from '../../../utils/logRocketUtils';
import useAuth from '../../../providers/Auth/Auth.provider';
import '../Clients.scss';
import { hasIdProperty } from '../../../components/TwFilters/TwFilters';

const columns: TwTableColumn[] = [
  { id: 'name', label: 'Name', minWidth: 150 },
  { id: 'email', label: 'Billing Email', minWidth: 150 },
  { id: 'phone', label: 'Primary Phone Number', minWidth: 100 },
  { id: 'orders', label: 'Orders completed', minWidth: 80 },
  { id: 'contacts', label: 'Contacts', minWidth: 80 },
  { id: 'menu', label: '', maxWidth: 10 }
];

const Clients: React.FC = (): JSX.Element => {
  usePageTitle('Companies | Truckbase');
  const {
    data: { userData }
  } = useAuth();

  const navigate = useNavigate();
  const { addNotification } = useNotifications();
  const { enableCustomerPortal } = useFeatureFlags();

  const [tableData, setTableData] = useState<TwTableData[]>([]);
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState<boolean>(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>('');

  const [itemsPerPage] = useState(10);
  const [page, setPage] = useState(1);
  const [pageParams, setPageParams] = useState<BaseQueryParams>({
    offset: 0,
    limit: itemsPerPage,
    deleted: false
  });

  const [filterOpen, setFilterOpen] = useState<string>('');
  const [filterParams, setFilterParams] = useState<ISearchClientParams | undefined>({});
  const { clients: twClients, fetchClients } = useTwClient(true);
  // RTK Query hook
  const { data: clients = { content: [], total: 0 }, isLoading } = useSearchCustomersQuery({
    pageParams,
    filterParams
  });
  const [deleteCustomer] = useDeleteCustomerMutation();

  useEffect(() => {
    setPage(1);
  }, [JSON.stringify(filterParams)]);

  useEffect(() => {
    const newOffset = (page - 1) * itemsPerPage;
    setPageParams({ ...pageParams, offset: newOffset, limit: itemsPerPage });
  }, [page]);

  useEffect(() => {
    // Insert column for customer portal between phone and menu column, if the portal column does not exist
    if (enableCustomerPortal && !columns.find((c) => c.id === 'portal')) {
      columns.splice(5, 0, {
        id: 'portal',
        label: 'Customer portal',
        maxWidth: 50
      });
    }
  }, [enableCustomerPortal]);

  useEffect(() => {
    transformClients(clients.content);
  }, [JSON.stringify(clients.content), enableCustomerPortal]);

  /** Changes page */
  const handlePageChange = (value: number): void => {
    setPage(value);
  };

  /**
   * Returns badge for customer portal status
   * @param status
   */
  const getBadge = (status: USER_STATUS): JSX.Element => {
    const label = (): string => {
      switch (status) {
        case USER_STATUS.ACTIVE:
          return 'Active';
        case USER_STATUS.INVITED:
          return 'Invite sent';
        case USER_STATUS.DISABLED:
          return 'Disabled';
        default:
          return 'Not invited';
      }
    };

    if (status === USER_STATUS.ACTIVE) {
      return <TwBadge content={label()} variant={'success'} />;
    } else {
      return <TwBadge content={label()} variant={'default'} />;
    }
  };
  /**
   * Menu available for each client
   */
  const ClientMenu: React.FC<{ client: ClientResponse }> = ({ client: { id } }): JSX.Element => {
    const options: TwGenericMenuOptions[] = [
      {
        label: 'Edit',
        action: (): void => {
          navigate(`/customers/${id}`);
        }
      },
      {
        label: 'Delete',
        className: 'clients__delete',
        action: (): void => {
          setSelectedCustomerId(id);
          setDeleteConfirmationOpen(true);
        }
      }
    ];
    return (
      <div className="clients__options">
        <TwGenericMenu options={options} />
      </div>
    );
  };

  /**
   * Creates table content from all clients
   * @param clients
   */
  const transformClients = (clients: ClientResponse[]): void => {
    const clientList: TwTableData[] = [];
    clients.forEach((client) => {
      const tableItem: TwTableData = {
        id: client.id,
        name: <TwNameColumn name={client.name} />,
        email: client.billing_email,
        phone: StringUtils.formatPhoneNumber(client.primary_phone ?? '', client.phone_ext),
        orders: client.orders_completed ? client.orders_completed : '0',
        contacts: client.contacts ? client.contacts.length : '0',
        menu: <ClientMenu client={client} />,
        handleClick: {
          onClick: () => navigate(`/customers/${client.id}`),
          ignore: ['menu']
        }
      };

      if (enableCustomerPortal) {
        tableItem.portal = getBadge(client.customer_portal_invite_status!);
      }

      clientList.push(tableItem);
    });

    setTableData(clientList);
  };

  /**
   * Deletes a client given its id
   * @param clientId
   */
  const onDeleteClient = async (clientId: string): Promise<void> => {
    try {
      deleteCustomer(clientId)
        .unwrap()
        .then((res) => {
          addNotification({
            message: 'Customer has been deleted',
            variant: 'success'
          });

          setDeleteConfirmationOpen(false);
        })
        .catch((err) => {
          console.error(err);
          logRocketTrack('Customer Management', {
            action: 'Delete customer',
            severity: 'medium',
            tenant_id: userData?.tenant?.id,
            company_name: userData?.tenant?.company_name,
            email: userData?.email_address,
            user_first_name: userData?.first_name,
            user_last_name: userData?.last_name
          });
          addNotification({
            message: 'Error at deleting customer, please try again',
            variant: 'error'
          });
        });
    } catch (e) {
      console.error(e);
    }
  };

  /** Closes Delete confirmation dialog */
  const handleConfirmationClose = (): void => {
    setDeleteConfirmationOpen(false);
  };

  const handleFilterClick = (event: React.MouseEvent<HTMLElement>): void => {
    setFilterOpen(event.currentTarget.getAttribute('name') as string);
  };

  return (
    <>
      {isLoading ? (
        <TwBackdrop isOpen={isLoading} />
      ) : (
        <Box sx={{ paddingTop: '1rem' }}>
          <div className="clients__filters-wrapper">
            <TwFilters
              name="Customer"
              filterType={TwFilterTypes.typeahead}
              typeaheadOptionList={twClients.map((c) => ({
                id: c.id,
                label: c.name,
                value: c.id
              }))}
              title="Name"
              onClear={(): void => {
                const clearedFilter = { ...filterParams };
                delete clearedFilter['ids'];
                setFilterParams(clearedFilter);
                fetchClients('');
              }}
              onSelectedOptions={(options): void => {
                if (!Array.isArray(options) && hasIdProperty(options)) {
                  setFilterParams({ ...filterParams, ids: [options.id as string] });
                }
              }}
              onClick={handleFilterClick}
              open={filterOpen === 'Customer'}
              onInputChange={(
                event: SyntheticEvent<Element, Event>,
                value: string | null
              ): void => {
                if (filterParams?.ids && value === '') {
                  const clearedFilter = { ...filterParams };
                  delete clearedFilter['ids'];
                  setFilterParams(clearedFilter);
                }
                fetchClients(value ? value : '');
              }}
            />
          </div>

          <TwTable columns={columns} data={tableData} className="clients__table" />

          <TwPaginator
            currentPage={page}
            items={clients?.total || 0}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
          />

          <TwDialog
            size="xs"
            title="Delete customer?"
            open={deleteConfirmationOpen}
            onClickClose={handleConfirmationClose}
            actions={[
              <TwButton
                key="1"
                type={TwButtonType.secondary}
                label="Cancel"
                action={(): void => setDeleteConfirmationOpen(false)}
              />,
              <TwButton
                key="2"
                type={TwButtonType.negative}
                label="Delete"
                action={(): Promise<void> => onDeleteClient(selectedCustomerId)}
              />
            ]}
          >
            <Box component="div" className="clients__confirmation-dialog-content">
              <WarningSign />
              <span className="clients__confirmation-dialog-text">
                Are you sure you want to delete this client? You can’t undo this action.
              </span>
            </Box>
          </TwDialog>
        </Box>
      )}
    </>
  );
};

export default Clients;
